import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { CapacityCheckInput, CapacityCheckResult } from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import { useAuth } from '../auth/auth-context.js';
import { checkCapacity } from './capacity-api.js';

/**
 * Asking the workshop's book whether a date can be kept.
 *
 * A QUERY AND NOT A MUTATION, even though the route is a POST: the call writes nothing, and the
 * same question asked twice gets the same answer. Keying it by the request itself is what makes
 * that true in the cache too — change a task, an estimate or the date and it is a different
 * question, so it is a different key.
 *
 * NOTHING HERE GOES THROUGH THE OFFLINE WRITE QUEUE, and that is the point rather than an
 * omission. A capacity answer replayed twenty minutes later was computed against a book that has
 * moved on, and a stale "yes" is how a shop promises a date it cannot keep. No answer is better.
 * `gcTime: 0` says the same thing about the cache: the answer is thrown away the moment the
 * screen that asked stops looking at it.
 *
 * A TAILOR NEVER FIRES THIS. `reports.operations.read` guards the route and is owner-and-manager,
 * so a karigar's request could only ever 403 — and a screen that renders its refusal while still
 * firing the request is teaching the API to log an error about something nobody did wrong.
 */

export const capacityKeys = {
  all: ['capacity'] as const,
  check: (request: CapacityCheckInput | null) => ['capacity', 'check', request] as const,
};

export function useCapacityCheck(
  request: CapacityCheckInput | null,
  options: { enabled?: boolean } = {},
): UseQueryResult<CapacityCheckResult> {
  const client = useApiClient();
  const { role } = useAuth();

  return useQuery({
    queryKey: capacityKeys.check(request),
    queryFn: () => checkCapacity(client, request as CapacityCheckInput),
    enabled: request !== null && role !== 'tailor' && (options.enabled ?? true),
    gcTime: 0,
  });
}
